<template>
  <BreadcrumbChevron :breadcrumbOptions="breadcrumbOptions" @onClicked="onClicked" />
</template>

<script lang="ts" setup>
import BreadcrumbChevron from '@/design-system/atoms/BreadcrumbChevron.vue'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useStore } from 'vuex'
import { BUILDING, BUILDING_PROPERTIES } from '@/interfaces/building.interface'
import { COMPLEX } from '@/interfaces/complex.interface'
import { REAL_ESTATE } from '@/interfaces/realEstate.interface'
import { setViewToMapview, setViewToPlanview } from '@/utils/views'
import { getRealmId } from '@/utils/realmId'
import { BreadcrumbOption, generateBreadcrumbOptions } from '@/utils/breadcrumbs'
import { LEVEL_PROPERTIES } from '@/interfaces/level.interface'
import { useCurrentFeatureFilters } from '@/composables/useFilters'
import { zoomToEntities } from '@/composables/useMapbox'
import { useGeneralStore } from '@/pinia'
import { ZoneProperties } from '@/services/zones.service'
import { LineGeoJsonProperties } from '@/services/lines.service'
import { useSelectionStore } from '@/pinia/selectionNew'

// Reactive variables (incl. composables)
const store = useStore()
const piniaStore = useGeneralStore()
const filters = useCurrentFeatureFilters()

// Computed (store)
const allBuildings = computed<BUILDING[]>(() => store.getters.allBuildings)
const allComplexes = computed<COMPLEX[]>(() => store.getters.allComplexes)
const allRealEstates = computed<REAL_ESTATE[]>(() => store.getters.allRealEstates)
const buildingLevels = computed(() => store.state.levelsForSelectedBuilding)
const isIndoorviewShowing = computed<boolean>(() => store.getters.isIndoorviewShowing)
const isMapviewShowing = computed<boolean>(() => store.getters.isMapviewShowing)
const isModelviewShowing = computed<boolean>(() => store.getters.isModelviewShowing)
const roomsForSelectedLevel = computed(() => store.state.roomsForSelectedLevel)
const selectedBuilding = computed<BUILDING_PROPERTIES>(() => store.state.mapview.selectedBuilding)
const selectedComplex = computed(() => store.state.mapview.selectedComplex)
const selectedLevel = computed(() => store.state.selectedLevel)
const selectedLine = computed<LineGeoJsonProperties | null>(() => piniaStore.selectedLine)
const selectedPoi = computed(() => store.state.selectedPoi)
const selectedRealEstate = computed(() => store.state.mapview.selectedRealEstate)
const selectedRoom = computed(() => store.state.selectedRoom)
const selectedZone = computed<ZoneProperties | null>(() => piniaStore.selectedZone)
const showOverlayModalPinia = computed(() => piniaStore.showOverlayModalPinia)
const dashboardBuildings = computed<BUILDING_PROPERTIES[]>(
  () => filters.value?.submitted?.buildings || []
)
const dashboardLevel = computed<LEVEL_PROPERTIES | undefined | null>(() =>
  filters.value && 'level' in filters.value.submitted ? filters.value?.submitted?.level : undefined
)
// Computed (component specific)
const realm = getRealmId()
const { t } = useI18n()
const selection = useSelectionStore()

const breadcrumbOptions = computed<BreadcrumbOption[]>(() =>
  generateBreadcrumbOptions({
    t: t as (key: string) => string,
    realm,
    selectedComplex: selectedComplex.value,
    selectedRealEstate: selectedRealEstate.value,
    selectedBuildings: [...dashboardBuildings.value.map((x) => x), selectedBuilding.value],
    selectedLevel: selectedLevel.value || dashboardLevel.value,
    selectedLine: selectedLine.value,
    selectedRoom: selectedRoom.value,
    selectedPoi: selectedPoi.value,
    selectedZone: selectedZone.value,
    roomsForSelectedLevel: roomsForSelectedLevel.value,
    isBuildingSelected: isBuildingSelected.value
  })
)
const isBuildingSelected = computed(
  () => Object.keys(store.state.mapview.selectedBuilding).length > 0
)
async function handleClickBuilding(option) {
  const building = allBuildings.value.find((x) => x.properties.id === option.id)
  if (!building) return
  await store.dispatch('CHANGE_BUILDING', { building })
  zoomToEntities([building.id])
}

async function handleClickComplex(option) {
  const complex = allComplexes.value.find((x) => x.properties.belongsToId === option.id)
  if (!complex) return
  await store.dispatch('CHANGE_COMPLEX', { complex: complex.properties })
  const features = allRealEstates.value.filter((x) => x.properties.complexId === option.id)
  zoomToEntities(features.map((x) => x.id))
}
async function handleClickLevel(option) {
  const level = buildingLevels?.value.data.features.find((x) => x.id === option.id)
  if (!level) return
  await store.dispatch('CHANGE_LEVEL', { levelNumber: level.properties.level })
  zoomToEntities([level.id])
}
async function handleClickRealEstate(option) {
  const realEstate = allRealEstates.value.find((x) => x.id === option.id)
  if (!realEstate) return
  await store.dispatch('CHANGE_REAL_ESTATE', { realEstate: realEstate.properties })
  zoomToEntities([realEstate.id])
}
async function handleClickRoom(option) {
  const room = roomsForSelectedLevel.value?.data.features.find((x) => x.id === option.id)
  if (!room) return
  await store.dispatch('CHANGE_ROOM', { roomId: option.id })
  zoomToEntities([room.id])
}
async function onClicked(option) {
  if (showOverlayModalPinia.value) piniaStore.setShowOverlayModalPinia(false)
  if (
    option.type !== 'several-complexes' &&
    option.type !== 'several-real-estates' &&
    option.type !== 'several-buildings'
  )
    store.commit('SET_CLICKED_ITEM_TYPE', { type: option.type })

  selection.set([option.id])

  if (option.type === 'complex') {
    if (!isMapviewShowing.value) {
      setViewToMapview()
    }
    await handleClickComplex(option)
  }
  if (option.type === 'realEstate') {
    if (!isMapviewShowing.value) {
      setViewToMapview()
    }
    await handleClickRealEstate(option)
  }
  if (option.type === 'building') {
    if (!isMapviewShowing.value) {
      setViewToMapview()
    }
    await handleClickBuilding(option)
  }
  if (option.type === 'level') {
    if (isIndoorviewShowing.value || isModelviewShowing.value) {
      setViewToPlanview()
    }
    await handleClickLevel(option)
  }
  if (option.type === 'room') {
    if (isIndoorviewShowing.value || isModelviewShowing.value) {
      setViewToPlanview()
    }
    await handleClickRoom(option)
  }
}

defineExpose({
  breadcrumbOptions
})
</script>
